import React, { useEffect, useState, useMemo } from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { CalendarDays, ListTodo, TrendingUp, Handshake, Wallet } from "lucide-react";

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      const [t, e, l, s, r] = await Promise.all([
        api.get("/tasks"), api.get("/events"), api.get("/leads"), api.get("/sponsors"), api.get("/receivables"),
      ]);
      const arr = [];
      t.data.forEach((x) => x.due_date && arr.push({ id: x.id, kind: "task", date: x.due_date, title: x.title, sub: x.assignee_name, status: x.status }));
      e.data.forEach((x) => x.date && arr.push({ id: x.id, kind: "event", date: x.date, title: x.name, sub: x.venue_name, status: x.status }));
      l.data.forEach((x) => x.next_follow_up && arr.push({ id: x.id, kind: "lead", date: x.next_follow_up, title: `Follow up ${x.company}`, sub: x.assigned_to_name, status: x.status }));
      s.data.forEach((x) => x.next_follow_up && arr.push({ id: x.id, kind: "sponsor", date: x.next_follow_up, title: `Follow up ${x.brand}`, sub: x.assigned_to_name, status: x.status }));
      r.data.forEach((x) => x.due_date && arr.push({ id: x.id, kind: "invoice", date: x.due_date, title: `Payment ${x.client}`, sub: x.invoice_number, status: x.status }));
      arr.sort((a, b) => (a.date > b.date ? 1 : -1));
      setItems(arr);
    })();
  }, []);

  const filtered = useMemo(() => filter === "all" ? items : items.filter((i) => i.kind === filter), [items, filter]);
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((i) => { g[i.date] = g[i.date] || []; g[i.date].push(i); });
    return Object.entries(g);
  }, [filtered]);

  const iconFor = (k) => ({ task: ListTodo, event: CalendarDays, lead: TrendingUp, sponsor: Handshake, invoice: Wallet }[k] || CalendarDays);
  const toneFor = (k) => ({ task: "blue", event: "emerald", lead: "amber", sponsor: "amber", invoice: "rose" }[k] || "slate");

  const KINDS = [
    { k: "all", label: "All" }, { k: "task", label: "Tasks" }, { k: "event", label: "Events" },
    { k: "lead", label: "Sales follow-ups" }, { k: "sponsor", label: "Sponsor follow-ups" }, { k: "invoice", label: "Payments" },
  ];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Calendar</div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Shared Timeline</h1>
      </div>
      <div className="flex gap-2 flex-wrap">
        {KINDS.map((k) => (
          <button key={k.k} data-testid={`filter-${k.k}`} onClick={() => setFilter(k.k)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors duration-200 ${filter === k.k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"}`}>
            {k.label}
          </button>
        ))}
      </div>
      {grouped.length === 0 ? (
        <div className="text-slate-500 text-sm">Nothing scheduled.</div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([date, list]) => (
            <Card key={date} className="border-slate-200"><CardContent className="p-4">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">{fmtDate(date)}</div>
              <ul className="space-y-2">
                {list.map((i) => {
                  const Icon = iconFor(i.kind);
                  return (
                    <li key={`${i.kind}-${i.id}`} className="flex items-start gap-3 text-sm">
                      <div className={`h-8 w-8 rounded-md bg-${toneFor(i.kind)}-50 text-${toneFor(i.kind)}-600 flex items-center justify-center`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">{i.title}</div>
                        <div className="text-xs text-slate-500">{i.sub}</div>
                      </div>
                      <StatusBadge status={i.status} />
                    </li>
                  );
                })}
              </ul>
            </CardContent></Card>
          ))}
        </div>
      )}
    </div>
  );
}
